/**
 * 誌面の代替テキスト（FR-A11Y・WCAG 1.1.1「非テキストコンテンツ」）。
 *
 * 誌面は canvas でしか描かれず、canvas は `aria-hidden` にしてあるため、表示中ページの
 * 中身をここへ文字として出す。テキスト層のある本は本文、無い本・遮断ページは見出し行
 * （「N ページ / 全 M」）だけになる。
 *
 * 置き場所は誌面（`role="application"`）の**外**。application の内側に置くと、支援技術が
 * 読み上げモードから外れて本文を読み進められない。
 *
 * ライブ領域にはしない — ページ遷移の告知は Announcer 1 本が担う（同時に可視なライブ領域を
 * 増やさない・M8c-2 手順 10 の不変条件）。
 */
import { cls } from '../brand';
import type { TextSegment } from '../source/text-layer';

export interface PageTextEntry {
  /** ページの見出し行（「N ページ / 全 M」）。 */
  heading: string;
  /**
   * 見出しに続けて出す行。テキスト層のある本は本文 1 行、遮断ページ（EP-5）は
   * 案内 2 行（表題と補足）、どちらも無ければ空＝見出し行だけの縮退。
   *
   * 本文の行は区切り列で渡す（WCAG 3.1.2）。区切りに `lang` が付いていれば、その部分だけ
   * `<span lang>` にする — 支援技術はそこだけ言語を切り替えて読む。
   */
  lines: readonly (string | readonly TextSegment[])[];
}

export class PageTextView {
  private readonly el: HTMLDivElement;

  constructor(private readonly doc: Document) {
    this.el = doc.createElement('div');
    this.el.className = `${cls('sr-only')} ${cls('page-text')}`;
  }

  /** ビューワのルート直下（誌面の直後）へ設置する。 */
  mount(root: HTMLElement, before: Node | null = null): void {
    root.insertBefore(this.el, before);
  }

  /**
   * 誌面本文の言語を器に置く（WCAG 3.1.2 の土台）。ホストページの言語が何であれ、
   * ここから下は誌面の言語で読まれる — 日本語のページに英語の本を載せた場合の実害を消す。
   */
  setLang(lang: string): void {
    if (lang === '') this.el.removeAttribute('lang');
    else this.el.lang = lang;
  }

  /** 表示中ページ（見開きなら 2 ページ分）の内容へ差し替える。 */
  set(entries: readonly PageTextEntry[]): void {
    this.el.textContent = '';
    for (const entry of entries) {
      this.el.appendChild(this.line(entry.heading));
      for (const line of entry.lines) {
        if (typeof line === 'string') {
          if (line !== '') this.el.appendChild(this.line(line));
        } else if (line.length > 0) {
          this.el.appendChild(this.segments(line));
        }
      }
    }
  }

  private line(text: string): HTMLParagraphElement {
    const p = this.doc.createElement('p');
    p.textContent = text; // 本文は誌面由来。HTML として解釈させない。
    return p;
  }

  /** 言語の付いた区切りだけ `<span lang>` に包む（付いていない部分は素の文字）。 */
  private segments(segments: readonly TextSegment[]): HTMLParagraphElement {
    const p = this.doc.createElement('p');
    for (const segment of segments) {
      if (segment.text === '') continue;
      if (segment.lang === undefined) {
        p.appendChild(this.doc.createTextNode(segment.text));
        continue;
      }
      const span = this.doc.createElement('span');
      span.lang = segment.lang;
      span.textContent = segment.text;
      p.appendChild(span);
    }
    return p;
  }

  dispose(): void {
    this.el.remove();
  }
}
